// FILE: src/components/case-studies/admin/CaseItemModals.js
// Modal forms for stage content: narrative sections, artifacts, question
// attachment (existing questions only — question CONTENT is edited in the
// existing Question Editor, per the no-parallel-editor rule), and per-
// question artifact references.

import React, { useEffect, useMemo, useState } from 'react';
import {
  adminLinkCaseQuestion, adminSetQuestionArtifacts,
  adminUpsertCaseArtifact, adminUpsertCaseSection,
} from '../../../services/caseStudyService';
import { loadAllQuestionsFromSupabase } from '../../../services/questionService';
import { S, T } from './caseAdminStyles';

const SECTION_TYPES = ['briefing', 'organization', 'project_context', 'constraints',
  'stakeholder_context', 'event_update', 'outcome_context'];
const ARTIFACT_TYPES = ['text', 'table', 'email', 'memo', 'status_report', 'risk_register',
  'stakeholder_matrix', 'backlog', 'chart', 'image', 'organization_chart', 'transcript', 'contract_excerpt'];
const IMAGE_TYPES = ['image', 'organization_chart', 'chart'];

function Modal({ title, onClose, children, footer }) {
  return (
    <div style={S.modalScrim} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={S.h1}>{title}</h2>
        {children}
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>{footer}</div>
      </div>
    </div>
  );
}

export function SectionModal({ caseId, stage, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    section_type: item?.section_type || 'briefing',
    title: item?.title || '', content: item?.content || '',
    sequence: item?.sequence || stage.sections.length + 1,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  async function save() {
    setBusy(true); setError(null);
    try {
      await adminUpsertCaseSection({
        ...(item ? { id: item.id } : {}), case_study_id: caseId, stage_id: stage.id, ...form,
      });
      onSaved();
    } catch (e) { setError(e.message); setBusy(false); }
  }
  return (
    <Modal title={item ? 'Edit section' : `Add section to Stage ${stage.sequence}`} onClose={onClose}
      footer={<>
        <button style={S.btn} disabled={busy} onClick={onClose}>Cancel</button>
        <button style={S.btnPrimary} data-testid="section-save" disabled={busy || !form.title.trim()} onClick={save}>
          {busy ? 'Saving…' : 'Save section'}
        </button>
      </>}>
      {error && <div style={S.err}>{error}</div>}
      <label style={S.label}>Type</label>
      <select style={S.input} value={form.section_type} onChange={(e) => setForm({ ...form, section_type: e.target.value })}>
        {SECTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <label style={S.label}>Title</label>
      <input style={S.input} data-testid="section-title" value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })} />
      <label style={S.label}>Content (markdown)</label>
      <textarea style={{ ...S.input, minHeight: 140 }} data-testid="section-content" value={form.content}
        onChange={(e) => setForm({ ...form, content: e.target.value })} />
    </Modal>
  );
}

export function ArtifactModal({ caseId, stage, item, onClose, onSaved }) {
  const [form, setForm] = useState({
    artifact_type: item?.artifact_type || 'table',
    title: item?.title || '', description: item?.description || '',
    asset_url: item?.asset_url || '', alt_text: item?.alt_text || '',
    sequence: item?.sequence || stage.artifacts.length + 1,
    contentText: item?.content_json ? JSON.stringify(item.content_json, null, 2) : '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const isImage = IMAGE_TYPES.includes(form.artifact_type);

  async function save() {
    let content_json = null;
    if (form.contentText.trim()) {
      try { content_json = JSON.parse(form.contentText); }
      catch (e) { setError(`Structured content is not valid JSON: ${e.message}`); return; }
    }
    if (isImage && form.asset_url && !form.alt_text.trim()) {
      setError('Image artifacts need alt text (publishing is blocked without it).'); return;
    }
    setBusy(true); setError(null);
    try {
      await adminUpsertCaseArtifact({
        ...(item ? { id: item.id } : {}), case_study_id: caseId, stage_id: stage.id,
        artifact_type: form.artifact_type, title: form.title, description: form.description,
        content_json, asset_url: form.asset_url || null, alt_text: form.alt_text || null,
        sequence: form.sequence,
      });
      onSaved();
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <Modal title={item ? 'Edit artifact' : `Add artifact to Stage ${stage.sequence}`} onClose={onClose}
      footer={<>
        <button style={S.btn} disabled={busy} onClick={onClose}>Cancel</button>
        <button style={S.btnPrimary} data-testid="artifact-save" disabled={busy || !form.title.trim()} onClick={save}>
          {busy ? 'Saving…' : 'Save artifact'}
        </button>
      </>}>
      {error && <div style={S.err} data-testid="artifact-error">{error}</div>}
      <label style={S.label}>Type</label>
      <select style={S.input} data-testid="artifact-type" value={form.artifact_type}
        onChange={(e) => setForm({ ...form, artifact_type: e.target.value })}>
        {ARTIFACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      <label style={S.label}>Title</label>
      <input style={S.input} data-testid="artifact-title" value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })} />
      <label style={S.label}>Description</label>
      <input style={S.input} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      {isImage && (<>
        <label style={S.label}>Asset URL (question-images bucket)</label>
        <input style={S.input} data-testid="artifact-asset-url" value={form.asset_url}
          onChange={(e) => setForm({ ...form, asset_url: e.target.value })} />
        <label style={S.label}>Alt text (required for images)</label>
        <input style={S.input} data-testid="artifact-alt-text" value={form.alt_text}
          onChange={(e) => setForm({ ...form, alt_text: e.target.value })} />
      </>)}
      <label style={S.label}>Structured content (JSON — tables: {'{columns, rows}'}; emails: {'{from, to, subject, body}'})</label>
      <textarea style={{ ...S.input, minHeight: 150, fontFamily: 'monospace', fontSize: '0.78rem' }}
        data-testid="artifact-content-json" value={form.contentText}
        onChange={(e) => setForm({ ...form, contentText: e.target.value })} />
    </Modal>
  );
}

export function AttachQuestionModal({ full, stage, stageModel, onClose, onSaved }) {
  const [bank, setBank] = useState(null);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const cert = full.case.certification;
  const linkedIds = useMemo(() => new Set((full.questions || []).map((x) => x.id)), [full]);
  const nextSeq = useMemo(() => Math.max(0, ...(full.questions || []).map((x) => x.case_sequence || 0)) + 1, [full]);

  useEffect(() => {
    let live = true;
    loadAllQuestionsFromSupabase(cert)
      .then((rows) => { if (live) setBank(rows || []); })
      .catch((e) => { if (live) { setError(e.message); setBank([]); } });
    return () => { live = false; };
  }, [cert]);

  const matches = useMemo(() => {
    if (!bank) return [];
    const needle = q.trim().toLowerCase();
    return bank
      .filter((row) => !needle || String(row.id).toLowerCase().includes(needle) ||
        (row.question || '').toLowerCase().includes(needle))
      .slice(0, 25);
  }, [bank, q]);

  async function attach(row) {
    setBusy(true); setError(null);
    try {
      await adminLinkCaseQuestion(row.id, full.case.id, stage.id, nextSeq);
      onSaved();
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <Modal title={`Attach question to Stage ${stage.sequence}: ${stage.title}`} onClose={onClose}
      footer={<button style={S.btn} onClick={onClose}>Close</button>}>
      {error && <div style={S.err}>{error}</div>}
      <div style={{ ...S.sub, marginBottom: 8 }}>
        Attach an existing {cert.toUpperCase()} question. To write or edit question content
        (stem, options, key, rationale, ECO metadata), use the Question Editor — this panel only manages linkage.
      </div>
      <input style={S.input} placeholder="Search by id or text…" value={q} data-testid="attach-search"
        onChange={(e) => setQ(e.target.value)} />
      <div style={{ marginTop: 10, maxHeight: 320, overflowY: 'auto' }} data-testid="attach-results">
        {bank === null && <div style={S.sub}>Loading question bank…</div>}
        {matches.map((row) => {
          const linkedHere = linkedIds.has(String(row.id));
          const linkedElsewhere = row.caseStudyId && row.caseStudyId !== full.case.id;
          const disabled = busy || linkedHere || linkedElsewhere;
          return (
            <div key={row.id} style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: '7px 9px', marginBottom: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.76rem', fontWeight: 700 }}>{row.id} <span style={S.chip(T.line, T.muted)}>{row.type}</span>
                  {linkedHere && <span style={{ ...S.chip(T.goodSoft, T.good), marginLeft: 6 }}>in this case</span>}
                  {linkedElsewhere && <span style={{ ...S.chip(T.warnSoft, '#8a6116'), marginLeft: 6 }}>in another case</span>}
                </div>
                <div style={{ fontSize: '0.78rem', color: T.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {(row.question || '').slice(0, 120)}
                </div>
              </div>
              <button style={S.btnPrimary} disabled={disabled} data-testid={`attach-${row.id}`}
                onClick={() => attach(row)}>Attach</button>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

export function QuestionArtifactRefsModal({ full, question, onClose, onSaved }) {
  const current = useMemo(() => (full.question_artifacts || [])
    .filter((r) => r.question_id === question.id)
    .reduce((m, r) => ({ ...m, [r.artifact_id]: r.display_mode }), {}), [full, question]);
  const [refs, setRefs] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const artifacts = full.artifacts || [];

  async function save() {
    setBusy(true); setError(null);
    try {
      await adminSetQuestionArtifacts(question.id,
        Object.entries(refs).map(([artifact_id, display_mode]) => ({ artifact_id, display_mode })));
      onSaved();
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <Modal title={`Artifact references — Q${question.case_sequence}`} onClose={onClose}
      footer={<>
        <button style={S.btn} disabled={busy} onClick={onClose}>Cancel</button>
        <button style={S.btnPrimary} data-testid="refs-save" disabled={busy} onClick={save}>
          {busy ? 'Saving…' : 'Save references'}
        </button>
      </>}>
      {error && <div style={S.err}>{error}</div>}
      {artifacts.length === 0 && <div style={S.sub}>No artifacts in this case yet.</div>}
      {artifacts.map((a) => (
        <div key={a.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${T.line}` }}>
          <input type="checkbox" id={`ref-${a.id}`} checked={a.id in refs} data-testid={`ref-check-${a.id}`}
            onChange={(e) => {
              const next = { ...refs };
              if (e.target.checked) next[a.id] = next[a.id] || 'reference'; else delete next[a.id];
              setRefs(next);
            }} />
          <label htmlFor={`ref-${a.id}`} style={{ flex: 1, fontSize: '0.82rem' }}>{a.title} <span style={S.chip(T.line, T.muted)}>{a.artifact_type}</span></label>
          {a.id in refs && (
            <select style={{ ...S.input, width: 'auto', padding: '3px 6px', fontSize: '0.76rem' }} value={refs[a.id]}
              onChange={(e) => setRefs({ ...refs, [a.id]: e.target.value })}>
              {['primary', 'reference', 'optional'].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
        </div>
      ))}
    </Modal>
  );
}
